'use client';
import classNames from 'classnames';
import {Header} from '@udemy-v2/react-header';
import {
    LayoutBody,
    LayoutContainer,
    LayoutTitleArea,
    Sidebar,
    SidebarNormalFlow,
    SidebarPanel,
    SidebarPanelUnit,
    SidebarPopout,
    StickyFooter,
} from '@udemy-v2/react-sidebar-layout-components';
import {ColorScheme, tokens} from '@udemy-v2/styles';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {useCommonData} from '@udemy-v2/common-data';
import {SubscriptionPlanProductType} from '@udemy-v2/subscription-graphql';

import {CourseBodyContent} from '@/features/course-landing-page/components/course-body-content/course-body-content.react-component';
import {CourseLede} from '@/features/course-landing-page/components/course-lede/course-lede.react-component';

import {Footer} from '@/components/footer';
import {SubscriptionMarketplacePurchaseContent} from '@/features/course-landing-page/components/marketplace-purchase-content/marketplace-purchase-content.react-component';
import {PremiumCourseNotice} from '@/features/course-landing-page/components/premium-course-notice/premium-course-notice.react-component';
import {StickyHeader} from '@/features/course-landing-page/components/sticky-header/sticky-header.react-component';
import {TeamsPurchaseContent} from '@/features/course-landing-page/components/teams-purchase-content/teams-purchase-content.react-component';
import {TeamsPurchaseSliderMenuContent} from '@/features/course-landing-page/components/teams-purchase-content/teams-purchase-slider-menu-content.react-component';

import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useClpPurchaseContext} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {useSubsDiff} from '@/features/course-landing-page/components/subs-diff/use-subs-diff.react-component';
import {useActivationOfferModal} from '@/features/course-landing-page/hooks/use-activation-offer-modal';
import {ActivationOfferModal} from '@udemy-v2/new-visitor-deal';

import styles from './course-landing-page.module.scss';
import {useEffect, memo, useMemo} from 'react';
import {publishEvent} from '@udemy-v2/event-tracking';
import {CLPViewEvent} from './events/events';
import {MarketplaceStickyFooterContent} from '@/features/course-landing-page/components/sticky-footer/marketplace-sticky-footer-content/marketplace-sticky-footer-content.react-component';
import {PositionMarkerProvider} from '@/components/position-marker';
import {
    CourseSidebarContainer,
    SIDEBAR_PREVIEW_UI_REGION,
} from '@/features/course-landing-page/components/course-sidebar/course-sidebar-container.react-component';
import {CoursePreviewEntryPoint} from '@/features/course-landing-page/components/course-preview-entry-point/course-preview-entry-point.react-component';
import {useSmartBarRef} from '@udemy-v2/react-smart-bar';

/**
 * Indicates whether or not a given piece of content is rendered in the sidebar or the body
 */
export enum CourseContentPlacement {
    SIDEBAR = 'sidebar',
    BODY = 'body',
}

/**
 * Isolated, memoized component to render sidebar preview
 * This prevents re-renders when parent CourseLandingPage re-renders
 * Always rendered but hidden via CSS on mobile to prevent mount/unmount cycles
 *
 * Note: CoursePreviewEntryPoint will still re-render when scroll position changes
 * (via PositionMarkerProvider context), which is expected and necessary for the
 * fade-out animation. The memo here prevents re-renders from parent state changes.
 */
const SidebarPreview = memo(function SidebarPreview() {
    return (
        <CoursePreviewEntryPoint
            uiRegion={SIDEBAR_PREVIEW_UI_REGION}
            hideWhenLedeOutOfView={true}
        />
    );
});

export const CourseLandingPage = () => {
    const {featureVariantAssignments} = useFeatures(['clp_outcome_above_video']);
    const course = useClpCourse();
    const commonData = useCommonData();
    const hasOrganization = !!commonData?.organization;
    const {
        activeSidebarTab,
        renderTabContainer,
        unwrappedConsumerSubscriptionPlan,
        useCollapsiblePurchaseSection,
        subscriptionTiersEnabled,
    } = useClpPurchaseContext();

    const isClpOutcomeAboveVideoEnabled =
        featureVariantAssignments.get('clp_outcome_above_video')?.configuration
            ?.showClpOutcomeAboveVideo ?? false;

    const isConsumerSubscriptionPlan =
        unwrappedConsumerSubscriptionPlan?.productType ===
            SubscriptionPlanProductType.Consumersubscription ||
        unwrappedConsumerSubscriptionPlan?.productType ===
            SubscriptionPlanProductType.ConsumersubscriptionValuePlan;
    const premiumCourseNotice =
        !subscriptionTiersEnabled && isConsumerSubscriptionPlan ? <PremiumCourseNotice /> : null;

    const isDesktop = useMatchMedia(`(min-width: ${tokens['viewport-lg-min']})`);
    const {shouldAddSubsDiffSpacing, shouldShowSubsDiff, isSubsDiffLoading, SubsDiffComponent} =
        useSubsDiff();
    const {smartBarHeight, isSticky, isHidden} = useSmartBarRef();

    const {isOpen, showModal, hideModal, canShowModal} = useActivationOfferModal();

    // Construct passwordless auth signup URL with action=nvd-login
    const signupUrl = useMemo(() => {
        const loginPath = '/join/passwordless-auth/';
        const params = new URLSearchParams({
            action: 'nvd-login',
            mode: 'marketplace-signup',
        });
        return `${loginPath}?${params.toString()}`;
    }, []);

    // Memoize smart bar offset style to prevent unnecessary re-renders
    const sidebarOffsetStyle = useMemo(
        () => ({
            marginBlockStart: isSticky && !isHidden ? `${smartBarHeight}px` : 0,
        }),
        [isSticky, isHidden, smartBarHeight],
    );

    const courseBodyContent = <CourseBodyContent />;

    useEffect(() => {
        if (course.frontendTrackingId) {
            publishEvent(
                new CLPViewEvent({
                    courseId: Number(course.id),
                    courseTrackingId: course.frontendTrackingId ?? '',
                    isDirectLanded: course.isDirectLanded ?? false,
                }),
            );
        }
    }, [course.id, course.frontendTrackingId, course.isDirectLanded]);

    useEffect(() => {
        if (canShowModal) showModal();
    }, [canShowModal, showModal]);

    // Render sidebar content without the preview (preview rendered separately to avoid re-renders)
    const renderSidebarPanelContent = (placement: CourseContentPlacement) => {
        const personalContent = (
            <SubscriptionMarketplacePurchaseContent
                placement={placement}
                premiumCourseNotice={premiumCourseNotice}
            />
        );
        const teamsContent = <TeamsPurchaseContent />;

        return (
            <CourseSidebarContainer
                placement={placement}
                personalContent={personalContent}
                teamsContent={teamsContent}
                renderTabContainer={renderTabContainer}
                useCollapsiblePurchaseSection={useCollapsiblePurchaseSection}
            />
        );
    };

    return (
        <PositionMarkerProvider>
            <div className={classNames('ud-full-viewport-container', styles['layout-container'])}>
                <Header />
                <StickyHeader
                    rating={course.rating}
                    title={course.title}
                    numEnrollments={course.enrollments?.count}
                />
                <LayoutContainer className={styles['layout-container']}>
                    <LayoutTitleArea className={styles['title-area']}>
                        <ColorScheme
                            colorScheme={isDesktop ? 'dark' : 'light'}
                            /* Transparent background prevents FOUC: useMatchMedia initializes as null,
                                causing ColorScheme to briefly render as 'light' on desktop.
                                The scaffolding mixin sets background-color: white on .ud-color-scheme-light,
                                flashing white before the dark ::before pseudo-element takes over. */
                            style={{backgroundColor: 'transparent'}}
                        >
                            <CourseLede
                                hideRating={shouldShowSubsDiff || isSubsDiffLoading}
                                hideEnrollments={shouldShowSubsDiff || isSubsDiffLoading}
                                className={classNames({
                                    [styles['course-lede-with-subs-diff']]:
                                        shouldAddSubsDiffSpacing,
                                })}
                            />
                            {!isDesktop && !hasOrganization && !isClpOutcomeAboveVideoEnabled && (
                                <SubsDiffComponent />
                            )}
                        </ColorScheme>
                    </LayoutTitleArea>
                    <Sidebar className={styles.sidebar} isSticky={true}>
                        {/* Render preview at sidebar level with smart bar offset - always mounted for LCP optimization */}
                        <div
                            className={styles['sidebar-preview-wrapper']}
                            style={sidebarOffsetStyle}
                        >
                            <SidebarPreview />
                        </div>
                        <SidebarPopout>
                            <SidebarPanel className={styles['sidebar-container']}>
                                <SidebarPanelUnit fullBleed>
                                    {/* Mobile/body placement - no preview, it's in the lede */}
                                    {renderSidebarPanelContent(CourseContentPlacement.SIDEBAR)}
                                </SidebarPanelUnit>
                            </SidebarPanel>
                        </SidebarPopout>
                        <SidebarNormalFlow>
                            <SidebarPanel className={styles['sidebar-container']}>
                                <SidebarPanelUnit fullBleed>
                                    {renderSidebarPanelContent(CourseContentPlacement.BODY)}
                                </SidebarPanelUnit>
                            </SidebarPanel>
                        </SidebarNormalFlow>
                    </Sidebar>
                    <StickyFooter className={styles['sticky-footer']}>
                        {activeSidebarTab === 'teams' ? (
                            <TeamsPurchaseSliderMenuContent />
                        ) : (
                            <MarketplaceStickyFooterContent />
                        )}
                    </StickyFooter>
                    <LayoutBody
                        className={
                            shouldAddSubsDiffSpacing
                                ? styles['body-container-with-subs-diff']
                                : styles['body-container']
                        }
                    >
                        {isDesktop && !hasOrganization && <SubsDiffComponent />}
                        {courseBodyContent}
                    </LayoutBody>
                </LayoutContainer>
                <Footer />
                {canShowModal && (
                    <ActivationOfferModal
                        isOpen={isOpen}
                        onClose={hideModal}
                        signupUrl={signupUrl}
                        method="GET"
                        enableAutoOpen
                    />
                )}
            </div>
        </PositionMarkerProvider>
    );
};
